import { useForm } from 'vee-validate'
import type {
  AditonalFormData,
  ElementalFormEmits,
  ElementalFormInstance,
  ElementalFormProps,
  ElementalFormValues,
} from './types'

import { useElementalFormContext } from '@/composables/elemental-form'

import { useSidebarStore } from '~~/src/stores/sidebar'
import { useElementalFormStore } from '~~/src/stores/elementalForm'
import { getInitialFormData } from '~~/src/utils/elemental-form'
import type { Elemental } from '~~/src/types/elemental'
import { INITIAL_ELEMENTAL, getElementalSection } from '~~/src/types/elemental'

export function useElementalForm(
  props: ElementalFormProps,
  emit: ElementalFormEmits,
): ElementalFormInstance {
  const elemental = toRef(props, 'elemental')
  const router = useRouter()
  const route = useRoute()
  const formStore = useElementalFormStore()
  const { goToView } = useSidebarStore()
  const formId = ref(randomID())

  const elementalType = computed(() => {
    return getElementalMetadata(
      getElementalType(props.elementaTypeId, props.elemental?.isList),
    )
  })

  const form = useForm<ElementalFormValues>({
    initialValues: getInitialFormData(
      INITIAL_ELEMENTAL,
      props.elementaTypeId,
      elemental,
    ),
    keepValuesOnUnmount: false,
  })

  const { handleSubmit, resetForm, values, setErrors } = form

  const debouncedUpdate = useDebounceFn((values) => {
    if (props.isSidebarView)
      formStore.updateFormValues(values)
  }, 300)

  watch(() => values, debouncedUpdate, { deep: true })

  const reloadElementalFormData = () => {
    const rawElemental = unref(elemental)
    if (!rawElemental?.id)
      return

    setErrors({})
    resetForm({
      values: getInitialFormData({}, props.elementaTypeId, rawElemental),
    })
  }

  const processFormValues = (values: ElementalFormValues) => {
    const processed = { ...values, video: values.video || '' }

    if (values.tutorialStep?.length) {
      processed.tutorialStep = values.tutorialStep.map((item, index) => ({
        ...item,
        orderIndex: index + 1,
      }))
    }

    return processed
  }

  const onSubmit = handleSubmit((values) => {
    const finalValues = processFormValues(values)
    emit('submit', finalValues as AditonalFormData & Elemental)
  })

  const cancel = () => {
    if (props.isSidebarView) {
      goToView()
    }
    else {
      if (route.params.id && elementalType.value?.name)
        router.push(`/${elementalType.value?.name}/${route.params.id}`)
      else
        router.push('/')
    }
    emit('cancel')
  }

  const hasElementalSection = computed(() =>
    Boolean(
      elementalType.value
        && getElementalSection(elementalType.value, 'isPremium', 'default'),
    ),
  )

  useElementalFormContext(props, {
    elemental: toRef(props, 'elemental'),
    elementalType,
    formId,
    onSubmit,
  })

  return {
    form,
    elementalType,
    onSubmit,
    cancel,
    reloadElementalFormData,
    formId,
    hasElementalSection,
  }
}
